/* eslint-disable no-console,@typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types,@typescript-eslint/explicit-function-return-type */
import dayjs from 'dayjs';

import * as Sentry from '@sentry/browser';

type LOG_LEVEL = 'log' | 'info' | 'warning' | 'success' | 'error';
/**
 * Logger with integrated Sentry support for logging different levels and contextual data.
 * Sentry is only active in production, not in development.
 */

const isDev = ['localhost', '127.0.0.1', '::1'].includes(
  window.location.hostname,
);

const baseLogger = {
  construct(level: LOG_LEVEL, message: string, ...data: any[]) {
    if (!isDev) {
      if (level === 'error') {
        this.captureSentryLog(level, message, data);
      }
      return;
    }
    // Define styles for each log level
    const logStyles = {
      log: 'color:rgb(207, 207, 207); font-weight: bold; background: #1d1d1d; padding: 2px 4px; border-radius: 3px;',
      info: 'color: #2196f3; font-weight: bold; background: #1d1d1d; padding: 2px 4px; border-radius: 3px;',
      warning:
        'color: #ff9800; font-weight: bold; background: #1d1d1d; padding: 2px 4px; border-radius: 3px;',
      success:
        'color: #4caf50; font-weight: bold; background: #1d1d1d; padding: 2px 4px; border-radius: 3px;',
      error:
        'color: #f44336; font-weight: bold; background: #1d1d1d; padding: 2px 4px; border-radius: 3px;',
    };

    // Construct log message with formatted level
    const logMessage = `%c[${level.toUpperCase()}] ${message}`;

    // Group logs in the console with collapsed output
    console.groupCollapsed(logMessage, logStyles[level]);

    // Log the current time
    console.log(
      '%cTime:',
      'color: gray; font-style: italic;',
      dayjs().format('DD-MM-YYYY HH:mm:ss'),
    );

    // Log any additional data if provided
    if (data.length > 0) {
      console.log(
        '%cAdditional Data:',
        'color: gray; font-style: italic;',
        ...data,
      );
    }

    // Log the stack trace
    console.trace('%cStack Trace:', 'color: gray; font-style: italic;');
    console.groupEnd();

    // Log the message to Sentry with appropriate level and context, only in production
  },

  // Function to handle different log levels with Sentry capture
  captureSentryLog(level: LOG_LEVEL, message: string, data: any[]) {
    const extraData = {...data, timestamp: dayjs().format()};

    // Log to Sentry based on the level
    if (level === 'error') {
      // Capture exception for error and warning logs
      Sentry.captureException(new Error(message), {
        level: 'error',
        extra: extraData,
      });
    }
  },

  // Convenience function for info level
  log(message: string, ...data: any[]) {
    this.construct('log', message, ...data);
  },
  // Convenience function for info level
  info(message: string, ...data: any[]) {
    this.construct('info', message, ...data);
  },

  // Convenience function for warning level
  warn(message: string, ...data: any[]) {
    this.construct('warning', message, ...data);
  },

  // Convenience function for success level
  success(message: string, ...data: any[]) {
    this.construct('success', message, ...data);
  },

  // Convenience function for error level
  error(message: string, ...data: any[]) {
    this.construct('error', message, ...data);
  },
};

// Exclude 'log' method
type Logger = Omit<typeof baseLogger, 'construct' | 'captureSentryLog'>;

const Logger: Logger = {...baseLogger};

export default Logger;
